import { Box, Button, chakra, Flex, IconButton, Text, VStack } from '@chakra-ui/react';
import type { DemoInvitationDto } from '@fluenceur/types';
import { ArrowRight, CalendarCheck, VideoCamera, Xmark } from 'iconoir-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookedNotice, BookingError, OnlineDot, SlotsToggle, VincentAvatar } from './demo-parts';
import { DemoSlotPicker } from './DemoSlotPicker';
import { useDemoBookingFlow, type DemoBookingFlow } from './use-demo-booking-flow';

const Intro = ({ onPlan, onLater }: { onPlan: () => void; onLater: () => void }) => {
  const { t } = useTranslation();
  return (
    <VStack align="stretch" gap={1}>
      <Text fontFamily="heading" fontSize="15px" fontWeight="semibold">
        {t('demoInvitation.capsule.title')}
      </Text>
      <Text fontSize="13px" lineHeight="1.45" color="navDark.subText" textWrap="pretty">
        {t('demoInvitation.capsule.description')}
      </Text>
      <Flex gap={2} mt="10px">
        <Button size="sm" bg="white" color="gray.900" _hover={{ bg: 'softCream' }} onClick={onPlan}>
          {t('demoInvitation.capsule.plan')}
          <ArrowRight width={15} height={15} />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          color="navDark.subText"
          _hover={{ bg: 'navDark.hoverBg', color: 'white' }}
          onClick={onLater}
        >
          {t('demoInvitation.later')}
        </Button>
      </Flex>
    </VStack>
  );
};

const Picking = ({ flow }: { flow: DemoBookingFlow }) => {
  const { t } = useTranslation();
  return (
    <VStack align="stretch" gap={1}>
      <Text fontFamily="heading" fontSize="15px" fontWeight="semibold">
        {t('demoInvitation.capsule.pickTitle')}
      </Text>
      <Text fontSize="13px" color="navDark.subText">
        {t('demoInvitation.capsule.pickSubtitle')}
      </Text>
      <Box mt="10px">
        <DemoSlotPicker flow={flow} tone="dark" />
      </Box>
      {flow.selectedLabels ? (
        <chakra.button
          type="button"
          onClick={flow.book}
          disabled={flow.isBooking}
          mt="10px"
          h="40px"
          pl="14px"
          pr="6px"
          borderRadius="10px"
          bg="white"
          color="gray.900"
          display="flex"
          alignItems="center"
          gap="10px"
          textAlign="left"
          cursor="pointer"
          _hover={{ bg: 'softCream' }}
          _disabled={{ opacity: 0.6, cursor: 'wait' }}
        >
          <CalendarCheck width={17} height={17} />
          <Flex flex="1" align="baseline" gap="6px" fontSize="13.5px" fontWeight="medium" minW={0}>
            {flow.isBooking ? t('demoInvitation.booking') : t('demoInvitation.confirm')}
            <Text as="span" fontFamily="heading" fontWeight="semibold" truncate>
              {flow.selectedLabels.day.toLowerCase()} · {flow.selectedLabels.time}
            </Text>
          </Flex>
          <Flex
            w="28px"
            h="28px"
            borderRadius="7px"
            bg="gray.900"
            color="white"
            align="center"
            justify="center"
          >
            <ArrowRight width={15} height={15} />
          </Flex>
        </chakra.button>
      ) : null}
      <BookingError flow={flow} tone="dark" />
      <Flex
        align="center"
        justify="space-between"
        mt="6px"
        fontSize="12px"
        color="navDark.textMuted"
      >
        <Flex align="center" gap="6px">
          <VideoCamera width={13} height={13} />
          {t('demoInvitation.meetByEmail')}
        </Flex>
        <SlotsToggle flow={flow} tone="dark" label={t('demoInvitation.otherSlots')} />
      </Flex>
    </VStack>
  );
};

/**
 * The floating invitation of the first days: a short offer, then the slots,
 * then the confirmation, without leaving the page. Dark glass over the content,
 * pinned bottom right.
 */
export const DemoCapsule = ({
  brandId,
  invitation,
}: {
  brandId: string;
  invitation: DemoInvitationDto;
}) => {
  const { t } = useTranslation();
  const [picking, setPicking] = useState(false);
  const flow = useDemoBookingFlow({ brandId, invitation, readSlots: picking });

  return (
    <Box
      role="dialog"
      aria-label={t('demoInvitation.ariaLabel')}
      position="fixed"
      right={6}
      bottom={6}
      zIndex="overlay"
      w="440px"
      maxW="calc(100vw - 32px)"
      borderRadius="20px"
      bg="rgba(9,10,12,.84)"
      backdropFilter="blur(24px) saturate(170%)"
      borderWidth="1px"
      borderColor="navDark.subBorder"
      p={4}
      display="flex"
      gap="14px"
      alignItems="flex-start"
      color="white"
      animation="fade-in 0.25s ease-out"
    >
      <Box position="relative" flex="none">
        <VincentAvatar size="44px" ringColor="navDark.aiText" />
        <OnlineDot />
      </Box>
      <Box flex="1" minW={0}>
        {flow.booking ? (
          <BookedNotice flow={flow} tone="dark" />
        ) : picking ? (
          <Picking flow={flow} />
        ) : (
          <Intro onPlan={() => setPicking(true)} onLater={flow.close} />
        )}
      </Box>
      <IconButton
        aria-label={t('common.close')}
        size="xs"
        variant="ghost"
        borderRadius="full"
        color="navDark.textMuted"
        _hover={{ bg: 'navDark.hoverBg', color: 'white' }}
        onClick={flow.close}
      >
        <Xmark width={15} height={15} />
      </IconButton>
    </Box>
  );
};
